import { useMemo, type ReactNode } from "react";
import { MarkdownReact } from "@/components/MarkdownReact";
import {
  formatQuoteLineTotal,
  formatQuoteUnitPrice,
  getQuoteDisplayTotals,
} from "@/lib/quoteCurrency";
import {
  WEIGHT_UNIT_ABBREVIATION,
  type PublicQuote,
  type PublicQuoteItemLine,
  type PublicQuoteLine,
  type PublicQuoteQuantityBreak,
} from "@forgepoint/shared";
import { formatDateOnly } from "@forgepoint/shared";
import { CoverLetterBlock } from "../components/CoverLetterBlock";
import {
  formatAddressLines,
  getPublicQuoteBrandName,
  getQuoteEffectiveDate,
  getBreakWeightUnit,
  getTotalWeightsByUnit,
  publicQuotePeopleNames,
} from "../utils";
import {
  PdfPageViewer,
  usePdfPageInfo,
  type QuoteSection,
} from "../components/PdfPageViewer";

interface PublicQuoteTemplateInjectionMolderProps {
  quote: PublicQuote;
  isExpired: boolean;
}

/** Accent color sampled from the source document's table header bands. */
const ACCENT_COLOR = "#7b1f2b";

const TABLE_COLGROUP = (
  <colgroup>
    <col style={{ width: "17%" }} />
    <col style={{ width: "37%" }} />
    <col style={{ width: "12%" }} />
    <col style={{ width: "16%" }} />
    <col style={{ width: "18%" }} />
  </colgroup>
);

/**
 * Formats a date as a long US date string, e.g. "June 22, 2026".
 * @param value - The date value to format
 * @returns The formatted date, or null for missing/invalid values
 */
const DATE_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  month: "long",
  day: "numeric",
  year: "numeric",
};

function formatDate(value?: Date | string | null): string | null {
  if (!value) {
    return null;
  }
  if (typeof value === "string") {
    return formatDateOnly(value, DATE_FORMAT_OPTIONS, "en-US");
  }
  if (Number.isNaN(value.getTime())) {
    return null;
  }
  return value.toLocaleDateString("en-US", DATE_FORMAT_OPTIONS);
}

/**
 * Looks up a quote-level custom field value by name, ignoring case.
 * @param quote - The hydrated quote data
 * @param key - The custom field name to match case-insensitively
 * @returns The trimmed string value, or null when unset
 */
function getCustomField(quote: PublicQuote, key: string): string | null {
  const fields = quote.customFields;
  if (!fields) {
    return null;
  }
  const lowerKey = key.toLowerCase();
  const matchingKey = Object.keys(fields).find(
    (k) => k.toLowerCase() === lowerKey,
  );
  const value = matchingKey ? fields[matchingKey] : undefined;
  if (value === null || value === undefined) {
    return null;
  }
  const text = Array.isArray(value) ? value.join(", ") : String(value);
  return text.trim() || null;
}

function getLetterheadIdentity(quote: PublicQuote): {
  name: string | null;
  addressLines: string[] | null;
} {
  const locationName = quote.location?.name?.trim() || null;
  const locationAddressLines = formatAddressLines(quote.location?.address);

  if (locationName || locationAddressLines) {
    return {
      name: locationName,
      addressLines: locationAddressLines,
    };
  }

  return {
    name: quote.organization?.name?.trim() || null,
    addressLines: formatAddressLines(quote.organization?.address),
  };
}

/**
 * Injection Molder public quote template.
 * Reproduces a letter-style quotation: letterhead with rule, date and
 * quotation/log numbers, recipient block, RE line, accent-banded part and
 * tooling tables split by SECTION lines, a bold total bar, validity fine
 * print, and a salutation signoff, with terms on a following page.
 * @param quote - The hydrated quote data
 * @param isExpired - Whether the quote has expired
 */
export function PublicQuoteTemplateInjectionMolder({
  quote,
  isExpired,
}: PublicQuoteTemplateInjectionMolderProps) {
  const { sections, repeatableHeader } = useInjectionMolderSections(
    quote,
    isExpired,
  );
  return (
    <PdfPageViewer
      sections={sections}
      repeatableHeader={repeatableHeader}
      watermark={quote.watermark}
    />
  );
}

interface InjectionMolderLayout {
  sections: QuoteSection[];
  repeatableHeader: ReactNode;
}

/**
 * Builds the pageable sections and repeatable letterhead for the layout.
 * @param quote - The hydrated quote data
 * @param isExpired - Whether the quote has expired
 * @returns Sections and a repeatable header for PdfPageViewer
 */
function useInjectionMolderSections(
  quote: PublicQuote,
  isExpired: boolean,
): InjectionMolderLayout {
  const lines = useMemo(() => quote.lines ?? [], [quote.lines]);
  const totalWeightsByUnit = useMemo(
    () => getTotalWeightsByUnit(lines),
    [lines],
  );

  const repeatableHeader = <Letterhead quote={quote} />;
  const sections: QuoteSection[] = [];

  if (quote.enableCoverLetter && quote.coverLetterMarkdown) {
    sections.push({
      key: "cover-letter",
      skipHeader: true,
      content: (
        <CoverLetterBlock
          coverLetterMarkdown={quote.coverLetterMarkdown}
          enableCoverLetter={quote.enableCoverLetter}
        />
      ),
    });
  }

  sections.push({
    key: "quotation-body",
    forcePageBreak: !!(quote.enableCoverLetter && quote.coverLetterMarkdown),
    content: (
      <QuotationBody
        quote={quote}
        lines={lines}
        totalWeightsByUnit={totalWeightsByUnit}
        isExpired={isExpired}
      />
    ),
  });

  if (quote.termsOfService) {
    sections.push({
      key: "terms",
      forcePageBreak: true,
      content: (
        <div>
          <h2
            className="mb-3 text-center text-sm font-bold uppercase"
            style={{ color: ACCENT_COLOR }}
          >
            Terms and Conditions
          </h2>
          <div className="prose prose-xs prose-slate max-w-none text-justify text-[10px] leading-snug text-gray-900">
            <MarkdownReact>{quote.termsOfService}</MarkdownReact>
          </div>
        </div>
      ),
    });
  }

  return { sections, repeatableHeader };
}

/** Letterhead: logo, org name/address strip, and accent rule (every page). */
function Letterhead({ quote }: { quote: PublicQuote }) {
  const { pageNumber, totalPages } = usePdfPageInfo();
  const identity = getLetterheadIdentity(quote);
  const addressStrip = [identity.name, identity.addressLines?.join(", ")]
    .filter(Boolean)
    .join(" — ");
  const locationLogoUrl = quote.location?.logoUrl ?? null;
  const shouldShowRightHeader = !!locationLogoUrl || totalPages > 1;

  return (
    <div className="mb-4">
      <div className="flex items-start justify-between gap-4">
        {quote.organization?.logoUrl ? (
          <img
            src={quote.organization.logoUrl}
            alt={`${quote.organization.name} logo`}
            className="h-14 max-w-44 object-contain"
          />
        ) : (
          <p
            className="text-xl font-bold uppercase"
            style={{ color: ACCENT_COLOR }}
          >
            {identity.name}
          </p>
        )}
        {shouldShowRightHeader && (
          <div className="flex min-w-24 flex-col items-end gap-1">
            {locationLogoUrl && (
              <img
                src={locationLogoUrl}
                alt={`${quote.location?.name ?? "Facility"} logo`}
                className="h-16 max-w-48 object-contain"
              />
            )}
            {totalPages > 1 && (
              <p className="text-[10px] text-gray-500">
                Page {pageNumber} of {totalPages}
              </p>
            )}
          </div>
        )}
      </div>
      {addressStrip && (
        <p className="mt-1 text-[10px] text-gray-600">{addressStrip}</p>
      )}
      <hr className="mt-1 border-t-2" style={{ borderColor: ACCENT_COLOR }} />
    </div>
  );
}

/** The flowing letter body: meta block, recipient, RE line, tables, signoff. */
function QuotationBody({
  quote,
  lines,
  totalWeightsByUnit,
  isExpired,
}: {
  quote: PublicQuote;
  lines: PublicQuoteLine[];
  totalWeightsByUnit: ReturnType<typeof getTotalWeightsByUnit>;
  isExpired: boolean;
}) {
  const displayTotals = getQuoteDisplayTotals(quote);
  const effectiveDate = formatDate(getQuoteEffectiveDate(quote));
  const logNumber = getCustomField(quote, "Log Number");

  return (
    <div className="text-gray-900">
      <div className="mb-4 text-right text-xs">
        {effectiveDate && <p>{effectiveDate}</p>}
        <p>
          Quotation No. {quote.quoteDisplayNumber}
          {quote.revision > 1 ? ` Rev ${quote.revision}` : ""}
          {logNumber ? <span> &nbsp;|&nbsp; Log #{logNumber}</span> : null}
        </p>
      </div>

      <RecipientBlock quote={quote} />

      {quote.name && (
        <p className="mb-2 text-xs font-bold">RE: Quotation — {quote.name}</p>
      )}

      <LineFlow quote={quote} lines={lines} />

      {quote.showTotals && (
        <TotalBar
          totalFormatted={displayTotals.totalFormatted}
          showTotalWeight={quote.showTotalWeight ?? false}
          totalWeightsByUnit={totalWeightsByUnit}
        />
      )}

      <FinePrint quote={quote} isExpired={isExpired} />
      <Signoff quote={quote} />
      {quote.isSigned && quote.signature && (
        <SignedBlock
          name={quote.signature.name}
          signedAt={quote.signature.signedAt}
        />
      )}
    </div>
  );
}

/** "To:" recipient block: attention lines, customer name, and address. */
function RecipientBlock({ quote }: { quote: PublicQuote }) {
  const attention =
    getCustomField(quote, "Attention") || publicQuotePeopleNames(quote);
  const customerName = quote.customerCompany?.name ?? null;
  const addressLines = formatAddressLines(quote.customerCompany?.address);

  if (!attention && !customerName && !addressLines) {
    return null;
  }

  return (
    <div className="mb-3 text-xs leading-snug">
      <p className="font-bold">To:</p>
      {attention && <p className="whitespace-pre-line">{attention}</p>}
      {customerName && <p>{customerName}</p>}
      {addressLines?.map((line) => (
        <p key={line}>{line}</p>
      ))}
    </div>
  );
}

/**
 * Renders the ordered quote lines as flowing content: NOTE lines become
 * paragraphs, SECTION lines become bold headings that start a new table, and
 * ITEM/DISCOUNT lines accumulate into accent-banded tables.
 */
function LineFlow({
  quote,
  lines,
}: {
  quote: PublicQuote;
  lines: PublicQuoteLine[];
}) {
  const elements: ReactNode[] = [];
  let tableRows: ReactNode[] = [];
  let tableIndex = 0;

  const flushTable = () => {
    if (tableRows.length === 0) {
      return;
    }
    elements.push(
      <LineItemTable key={`table-${tableIndex}`} rows={tableRows} />,
    );
    tableRows = [];
    tableIndex += 1;
  };

  lines.forEach((line, index) => {
    if (line.type === "SECTION") {
      flushTable();
      if (line.description) {
        elements.push(
          <p
            key={`section-${index}`}
            className="mt-4 mb-1 text-[20px] font-bold"
          >
            {line.description}
          </p>,
        );
      }
      return;
    }

    if (line.type === "NOTE") {
      flushTable();
      if (line.description) {
        elements.push(
          <div
            key={`note-${index}`}
            className="prose prose-xs prose-slate my-2 max-w-none text-xs text-gray-900"
          >
            <MarkdownReact>{line.description}</MarkdownReact>
          </div>,
        );
      }
      return;
    }

    if (line.type === "DISCOUNT") {
      if (!quote.showTotals) {
        return;
      }
      const discountFormatted =
        line.amount !== null ? formatQuoteLineTotal(line, quote) : null;
      tableRows.push(
        <tr key={`discount-${index}`}>
          <td colSpan={4} className="px-2 py-1 text-xs font-semibold">
            {line.description || "Discount"}
          </td>
          <td className="px-2 py-1 text-right text-xs font-semibold">
            {discountFormatted ? `-${discountFormatted}` : ""}
          </td>
        </tr>,
      );
      return;
    }

    tableRows.push(
      <ItemRows key={`item-${index}`} line={line} quote={quote} />,
    );
  });

  flushTable();
  return <>{elements}</>;
}

/** A line-item table with the accent header band and striped rows. */
function LineItemTable({ rows }: { rows: ReactNode[] }) {
  return (
    <table className="my-2 w-full border-collapse text-xs [&_tbody_tr:nth-child(even)]:bg-[#f7eef0]">
      {TABLE_COLGROUP}
      <thead>
        <tr
          className="text-left text-[10px] font-bold text-white"
          style={{ backgroundColor: ACCENT_COLOR }}
        >
          <th className="px-2 py-1">Part</th>
          <th className="px-2 py-1">Description</th>
          <th className="px-2 py-1 text-right">Qty</th>
          <th className="px-2 py-1 text-right">Unit Price</th>
          <th className="px-2 py-1 text-right">Extended</th>
        </tr>
      </thead>
      <tbody className="[&>tr]:border-b [&>tr]:border-gray-200">{rows}</tbody>
    </table>
  );
}

/** Renders an ITEM line's quantity-break rows (part, description, pricing). */
function ItemRows({
  line,
  quote,
}: {
  line: PublicQuoteItemLine;
  quote: PublicQuote;
}) {
  const itemName = line.item?.name ?? line.description ?? "Item";
  const description =
    line.description && line.description !== itemName
      ? line.description
      : (line.item?.description ?? "");

  return (
    <>
      {line.quantityBreaks.map((qb, qbIndex) => {
        const quantity = qb.quantity?.trim();
        const unitPrice = qb.unitPriceOverride ?? qb.unitPrice;
        const weight = formatBreakWeight(line, qb);
        const spansDescription = !description && !weight;
        return (
          <tr key={`qb-${qbIndex}`} className="align-top">
            <td
              className="px-2 py-1 font-semibold"
              colSpan={spansDescription ? 2 : 1}
            >
              {qbIndex === 0 ? itemName : ""}
            </td>
            {!spansDescription && (
              <td className="whitespace-pre-line px-2 py-1">
                {qbIndex === 0 ? (
                  <>
                    {description}
                    {weight && (
                      <span className="block text-[10px] text-gray-500">
                        Weight: {weight}
                      </span>
                    )}
                  </>
                ) : (
                  ""
                )}
              </td>
            )}
            <td className="px-2 py-1 text-right">
              {quantity && !Number.isNaN(Number(quantity))
                ? new Intl.NumberFormat("en-US").format(Number(quantity))
                : (quantity ?? "")}
            </td>
            <td className="px-2 py-1 text-right">
              {unitPrice ? formatQuoteUnitPrice(qb, quote) : ""}
            </td>
            <td className="px-2 py-1 text-right">
              {qb.amount ? formatQuoteLineTotal(qb, quote) : ""}
            </td>
          </tr>
        );
      })}
    </>
  );
}

/**
 * Formats a quantity break's total weight with its unit, e.g. "1,050 lbs".
 * @param line - The item line the break belongs to
 * @param quantityBreak - The quantity break carrying the total weight
 * @returns The formatted weight, or null when unset
 */
function formatBreakWeight(
  line: PublicQuoteItemLine,
  quantityBreak: PublicQuoteQuantityBreak,
): string | null {
  const { totalWeight } = quantityBreak;
  if (!totalWeight) {
    return null;
  }
  const parsed = Number(totalWeight);
  const formatted = Number.isFinite(parsed)
    ? parsed.toLocaleString("en-US")
    : totalWeight;
  const unit = getBreakWeightUnit(quantityBreak, line);
  return unit ? `${formatted} ${WEIGHT_UNIT_ABBREVIATION[unit]}` : formatted;
}

/** Bold total bar with an accent top rule, plus optional total weights. */
function TotalBar({
  totalFormatted,
  showTotalWeight,
  totalWeightsByUnit,
}: {
  totalFormatted: string;
  showTotalWeight: boolean;
  totalWeightsByUnit: ReturnType<typeof getTotalWeightsByUnit>;
}) {
  return (
    <div className="mt-3 border-t-2 pt-1" style={{ borderColor: ACCENT_COLOR }}>
      <div className="flex items-baseline justify-between text-sm font-bold">
        <span>Total</span>
        <span>{totalFormatted}</span>
      </div>
      {showTotalWeight &&
        totalWeightsByUnit.map(([unit, weight]) => (
          <div
            key={unit}
            className="flex items-baseline justify-between text-xs"
          >
            <span>Total Weight ({WEIGHT_UNIT_ABBREVIATION[unit]})</span>
            <span>
              {Number(weight.toFixed(2)).toLocaleString("en-US", {
                minimumFractionDigits: 2,
              })}
            </span>
          </div>
        ))}
    </div>
  );
}

/** Fine print: validity, payment/delivery terms, and the quote footer. */
function FinePrint({
  quote,
  isExpired,
}: {
  quote: PublicQuote;
  isExpired: boolean;
}) {
  const expirationDate = formatDate(quote.expirationDate);
  const parts = [
    expirationDate && `Quotation valid until ${expirationDate}.`,
    quote.paymentTerms,
    quote.deliveryTerms && `Delivery: ${quote.deliveryTerms}.`,
    quote.quoteFooter,
  ].filter(Boolean);

  if (parts.length === 0 && !isExpired) {
    return null;
  }

  return (
    <div className="mt-3 text-[9px] leading-snug text-gray-600">
      <p>{parts.join(" ")}</p>
      {isExpired && (
        <p className="font-bold text-red-600">This quotation has expired.</p>
      )}
    </div>
  );
}

/** Salutation signoff with the sales rep, facility brand, and contact line. */
function Signoff({ quote }: { quote: PublicQuote }) {
  const companyLine = getPublicQuoteBrandName(quote);
  const contactLine = [quote.organization?.phone, quote.salesRepEmail]
    .filter(Boolean)
    .join(" | ");

  if (!quote.salesRepName && !companyLine && !contactLine) {
    return null;
  }

  return (
    <div className="mt-7 text-xs">
      <p>Sincerely,</p>
      <div className="mt-5">
        {quote.salesRepName && (
          <p className="font-signature text-2xl leading-none text-gray-900">
            {quote.salesRepName}
          </p>
        )}
        {companyLine && (
          <p
            className={
              quote.salesRepName
                ? "mt-2 font-semibold text-gray-800"
                : "font-semibold text-gray-800"
            }
          >
            {companyLine}
          </p>
        )}
        {contactLine && <p className="mt-1 text-gray-700">{contactLine}</p>}
      </div>
    </div>
  );
}

/** Signature confirmation block for already-signed quotes. */
function SignedBlock({
  name,
  signedAt,
}: {
  name: string;
  signedAt: Date | string;
}) {
  return (
    <div className="mt-4 border-t border-gray-300 pt-2 text-xs">
      <p className="text-gray-600">Accepted by:</p>
      <p className="font-signature text-xl">{name}</p>
      <p className="text-gray-600">
        Signed {new Date(signedAt).toLocaleDateString()}
      </p>
    </div>
  );
}
